import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useVvsFarmBoostContract } from 'hooks/useContract'
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from 'react-query'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import { QueryKey } from 'config/constants/queryKey'
import { BigNumber } from 'bignumber.js'
import { BigNumber as EtherBigNumber, ContractTransaction } from 'ethers'
import { FAST_INTERVAL } from 'contexts/RefreshContext'
import type { VVSFarmBoost, VvsFarmBoost } from '../../../types/ethers-contracts/VvsFarmBoost'

export const useCalculateTotalMultiplier = (farmId = 0) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  return useQuery([QueryKey.FARM_BOOST_CALCULATE_TOTAL_MULTIPLIER, account, farmId], async () => {
    if (!account || isNilOrUndef(farmId)) return 0
    const res = await vvsFarmBoostContract.calculateTotalMultiplier(account, farmId)

    return res.toNumber()
  })
}

export const queryKeyGetBoostedFarms = (account: string) => [QueryKey.GET_BOOSTED_FARMS, account]
export const queryGetBoostedFarms = async (vvsFarmBoostContract: VvsFarmBoost, account: string) => {
  if (!account) return []
  const res = await vvsFarmBoostContract.getBoostedFarms(account)

  return res.map((bn) => bn.toNumber())
}
export const useGetBoostedFarms = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  return useQuery(queryKeyGetBoostedFarms(account), () => queryGetBoostedFarms(vvsFarmBoostContract, account), {
    enabled: !!account,
  })
}

export const useHarvestAllBoosts = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(async () => {
    const tx = await vvsFarmBoostContract['harvestBoostAndWithdraw()']()
    const receipt = await tx.wait()

    return { tx, receipt }
  })
}

export const useGetBoostByPid = (farmId) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  return useQuery(
    [QueryKey.HARVEST_BOOST_BY_PID, farmId, account],
    async () => {
      if (isNilOrUndef(farmId)) return new BigNumber(0)
      const rewards = await vvsFarmBoostContract.callStatic['harvestBoost(uint256)'](farmId)
      return new BigNumber(rewards.toString())
    },
    {
      enabled: !!account,
      refetchInterval: FAST_INTERVAL,
    },
  )
}

export const useHarvestBoostByPid = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  const queryClient = useQueryClient()
  return useMutation(
    async (farmId: number) => {
      const tx = await vvsFarmBoostContract['harvestBoostAndWithdraw(uint256)'](farmId)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: (data, farmId) => {
        queryClient.invalidateQueries([QueryKey.HARVEST_BOOST_BY_PID, farmId, account])
        queryClient.invalidateQueries([QueryKey.WITHDRAW_BOOST_REWARD, account])
      },
    },
  )
}

export const useMoveBoost = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()
  return useMutation(
    async (params: { oldPid: number; newPid: number }) => {
      const { oldPid, newPid } = params

      const tx = await vvsFarmBoostContract.moveBoost(oldPid, newPid)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.GET_BOOSTED_FARMS)
      },
    },
  )
}

export const useMoveMultipleBoosts = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(async (params: { oldPids: number[]; newPids: number[] }) => {
    const { oldPids, newPids } = params

    const tx = await vvsFarmBoostContract.moveMultipleBoosts(oldPids, newPids)
    const receipt = await tx.wait()

    return { tx, receipt }
  })
}

export const useBoostMultipleFarms = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()
  return useMutation(
    async (params: {
      boostInfoList: Array<{
        pid: number
        nfts: any[]
      }>
    }) => {
      const { boostInfoList } = params

      const tx = await vvsFarmBoostContract.boostMultipleFarms(boostInfoList)
      const receipt = await tx.wait()

      return { receipt, tx }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.GET_BOOSTED_FARMS)
      },
    },
  )
}

export const useStakeNftsAndBoostFarm = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await vvsFarmBoostContract.stakeNftsAndBoostFarm(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.FARM_BOOST_USER_INFO)
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        queryClient.invalidateQueries(QueryKey.GET_BOOSTED_FARMS)
        queryClient.invalidateQueries(QueryKey.MY_MOLE_IDS_WITH_STAKED)
      },
    },
  )
}

export const useRemoveBoostAndUnstakeNfts = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(async (params: { farmId: number }) => {
    const { farmId } = params

    const tx = await vvsFarmBoostContract.removeBoostAndUnstakeNfts(farmId)
    const receipt = await tx.wait()

    return { tx, receipt }
  })
}

export const useGetActualAdjustShares = (farmId = 0, shouldRefetch = false) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  return useQuery(
    [QueryKey.GET_ACTUAL_ADJUST_SHARE, account, farmId],
    async () => {
      if (!account || isNilOrUndef(farmId)) return new BigNumber(0)
      const res = await vvsFarmBoostContract.getActualAdjustedShare(account, farmId)

      return new BigNumber(res.toString())
    },
    {
      refetchInterval: shouldRefetch ? FAST_INTERVAL : false,
    },
  )
}

export const useGetPotentialAdjustShares = (
  farmId: number,
  boostBalanceAmount: BigNumber,
  farmPosition: BigNumber,
  nfts: { tokenContract: string; tokenId: string }[] = [],
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()

  return useQuery(
    [QueryKey.GET_POTENTIAL_ADJUST_SHARE, account, farmId, boostBalanceAmount, farmPosition, nfts],
    async () => {
      if (isNilOrUndef(farmId) || isNilOrUndef(nfts) || isNilOrUndef(boostBalanceAmount) || isNilOrUndef(farmPosition))
        return new BigNumber(0)

      const res = await vvsFarmBoostContract.getPotentialAdjustedShare(
        account ?? '0x0000000000000000000000000000000000000000',
        farmId,
        EtherBigNumber.from(boostBalanceAmount.toString()),
        EtherBigNumber.from(farmPosition.toString()),
        nfts,
      )

      return new BigNumber(res.toString())
    },
    {
      enabled: !!farmId && !!boostBalanceAmount && !!farmPosition && !!nfts && (queryOptions?.enabled ?? true),
      ...queryOptions,
    },
  )
}

export const useGetFarmBoostPoolInfo = (farmId = 0, queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useQuery(
    [QueryKey.FARM_BOOST_POOL_INFO, farmId],
    async () => {
      if (isNilOrUndef(farmId)) return 0
      const res = await vvsFarmBoostContract.getPoolInfo(farmId)

      return res
    },
    {
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    },
  )
}

export const getFarmUserInfoQueryKey = (account: string, farmId: number) => [
  QueryKey.FARM_BOOST_USER_INFO,
  account,
  farmId,
]
export type BoostedFarmUserInfo = {
  farmId: number
  userInfo: VVSFarmBoost.UserInfoStructOutput
}
export const getFarmUserInfoQueryFn = async (
  farmId: number | undefined,
  account: string | undefined,
  vvsFarmBoostContract: VvsFarmBoost,
): Promise<BoostedFarmUserInfo> => {
  if (!account || isNilOrUndef(farmId)) return [0, 0, []] as any
  const res = await vvsFarmBoostContract.getUserInfo(farmId, account)

  return { farmId, userInfo: res }
}

export const useGetFarmUserInfo = (farmId = 0, queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  return useQuery(
    getFarmUserInfoQueryKey(account, farmId),
    () => getFarmUserInfoQueryFn(farmId, account, vvsFarmBoostContract),
    {
      enabled: !!account && (queryOptions?.enabled ?? true),
      ...queryOptions,
    },
  )
}

export const useUnStakeNfts = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await vvsFarmBoostContract.unstakeNfts(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.FARM_BOOST_USER_INFO)
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        queryClient.invalidateQueries(QueryKey.MY_MOLE_IDS_WITH_STAKED)
      },
    },
  )
}

export const useUpdateShares = (
  settleCallback?: () => void,
  successCallBack?: (tx: ContractTransaction) => void,
  errorCallback?: () => void,
) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number }) => {
      const { farmId } = params

      const tx = await vvsFarmBoostContract['updateShares(address,uint256)'](account, farmId)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: ({ tx }) => {
        queryClient.invalidateQueries(QueryKey.FARM_BOOST_USER_INFO)
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        successCallBack?.(tx)
      },
      onSettled: () => {
        settleCallback?.()
      },
      onError: () => {
        errorCallback?.()
      },
    },
  )
}
